/**
 * Component related commands
 */
import {MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import type {Component, Editor, StyleProps} from 'grapesjs';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {addUserTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager.reducer';
import type {UserEmailComponentTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import CropData from '../vos/CropData';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';
import type {BasicComponentCommandProps, EmailPreviewResponse, StyleAttributes} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import type {ComponentAlignmentStyle} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {ResponsiveAttributeName, ResponsiveType} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {hydrateMergeTagsReduxFromComponent} from '@/email-maker/grapesjs-email-editor-plugin/src/commands/editor';
import {xssCleanHtml} from '@Utils/string.util';
import store from '@/store';
import PresetComponentVO from '@/email-maker/grapesjs-email-editor-plugin/src/vos/presetcompvo';
import {isUserLoggedIn} from '@Libraries/user.library';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import juice from 'juice';
import type {TextComponentData} from '@Libraries/email-campaigns-library';
import {getEmailTextWithCcid} from '@Libraries/email-campaigns-library';
import {UNSUPPORTED_EMAIL_STYLES} from '@/email-maker/grapesjs-email-editor-plugin/src/var';

interface CellPropertyUpdateProps {
  global?: boolean;
  sliderVal?: number;
  cell?: Component;
  color?: string;
}

interface CustomStorageData {
  data: StorageDataFrame;
}

type StorageDataFrame = Record<any, any>;

interface EmailUploadResponseProps {
  identifier: string;
  imgResponse: EmailUploadResponse;
}

interface EmailUploadResponse {
  src: string;
  ext: string;
  filename: string;
}

interface StyleSetterProps {
  property: string;
  value: string;
}

interface ComponentWithURLMeta {
  component: Component;
  url: string;
  source: string;
}

interface BackgroundRemoveURLProps {
  hashedFilename: string;
  source: string;
  component: Component;
  backgroundRemovedURL: string;
  isBackground?: boolean;
  callback?: (url: string) => void;
}

interface ImageMeta {
  url: string;
  width: number;
  height: number;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const cmds = editor.Commands;
  const REDUNDANT_COMPONENTS = ['postermywall-drag-plh'];

  const CMDS = window.PMW.emails.commands;

  const ALIGNMENT_CENTER = 'center';
  const ALIGNMENT_RIGHT = 'right';
  const ALIGNMENT_LEFT = 'left';

  const placeMainContent = (): void => {
    const mainWrapper = editor.addComponents({
      type: window.PMW.emails.components.container.PMW_WRAPPER,
    })[0];

    const mainComponent = mainWrapper.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT}"]`)[0];

    editor.select(mainWrapper);

    editor.UndoManager.remove(mainWrapper);
    editor.UndoManager.remove(mainComponent);
    editor.UndoManager.clear();

    editor.runCommand(CMDS.UPDATE_UNDO_REDO_STATES);
  };

  const isSliderActive = (): boolean => {
    return $('.pmw-slider:active').length > 0;
  };

  const getComponentBgColor = (component: Component): string => {
    const styles = component.getStyle();
    return typeof styles['background-color'] !== 'undefined' ? (styles['background-color'] as string) : '#FFFFFF';
  };

  const getComponentBgImage = (component: Component): string => {
    const styles = component.getStyle();
    return typeof styles['background-image'] !== 'undefined' ? (styles['background-image'] as string) : 'none';
  };

  const hasBackgroundImage = (component: Component): boolean => {
    const backgroundImage = getComponentBgImage(component);
    return (backgroundImage && backgroundImage !== 'none') as boolean;
  };

  const getComponentBgClip = (component: Component): string => {
    const styles = component.getStyle();

    if (styles) {
      return styles['background-clip'] !== 'undefined' ? (styles['background-clip'] as string) : '';
    }

    return '';
  };

  const backwardsCompatibilityForDivs = (): void => {
    const wrapper = editor.DomComponents.getWrapper();
    if (!wrapper) {
      return;
    }

    const mainComponent = wrapper.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT}"]`)[0];
    const oldWrapper = editor.runCommand(CMDS.GET_MAIN_WRAPPER) as Component;
    const newWrapperContainer = wrapper.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT_CONTAINER}"]`);

    if (mainComponent && oldWrapper && newWrapperContainer.length === 0) {
      const components = mainComponent.components();

      oldWrapper.replaceWith({
        type: window.PMW.emails.components.container.PMW_WRAPPER,
      });

      const newWrapper = wrapper.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT_CONTAINER}"]`)[0];

      if (!newWrapper) {
        return;
      }

      components.each((child) => {
        newWrapper.components().add(child);
      });

      if (editor.runCommand(CMDS.IS_USER_OWNER, {})) {
        editor.runCommand(CMDS.SAVE_TO_CACHE, {});
      }
    }
  };

  const isGlobalStyleChangeApplicable = (table: Component, cell: Component, parentProperty: string, cellProperty: string): boolean => {
    if (table && cell) {
      const tableStyle = table.getStyle();
      const cellStyle = cell.getStyle();
      const propertiesExist = Object.prototype.hasOwnProperty.call(tableStyle, parentProperty) && Object.prototype.hasOwnProperty.call(cellStyle, cellProperty);
      const propertiesMatch = tableStyle[parentProperty] === cellStyle[cellProperty];

      if (propertiesExist && !propertiesMatch) {
        return false;
      }
    }

    return true;
  };

  const isRightAligned = (styles: StyleProps): boolean => {
    return (
      typeof styles['margin-left'] !== 'undefined' &&
      styles['margin-left'] === 'auto' &&
      typeof styles['margin-right'] !== 'undefined' &&
      parseInt(styles['margin-right'] as string, 10) === 0
    );
  };

  const isLeftAligned = (styles: StyleProps): boolean => {
    return (
      typeof styles['margin-left'] !== 'undefined' &&
      parseInt(styles['margin-left'] as string, 10) === 0 &&
      typeof styles['margin-right'] !== 'undefined' &&
      styles['margin-right'] === 'auto'
    );
  };

  const isCenterAligned = (styles: StyleProps): boolean => {
    return (
      (typeof styles.margin !== 'undefined' && styles.margin === 'auto') ||
      (typeof styles['margin-left'] !== 'undefined' && styles['margin-left'] === 'auto' && typeof styles['margin-right'] !== 'undefined' && styles['margin-right'] === 'auto')
    );
  };

  /**
   *
   * @param {string} hashedFilename
   * @param {string} source
   * @param {string} type
   * @returns {Promise<*>}
   */
  const createEmailMediaPresets = async (hashedFilename: string, source: string, type: string): Promise<any> => {
    if (!editor.StorageManager.isAutosave()) {
      await editor.store({});
    }

    return window.PMW.writeLocal('emailmaker/createEmailMediaPresets', {
      idEmail: window.PMW.idEmailTemplate,
      source,
      hashedFilename,
      type,
    });
  };

  const doesComponentHaveTagName = (component: Component, tagName: string): boolean => {
    if (component) {
      return component.get('tagName')?.toUpperCase() === tagName;
    }

    return false;
  };

  const handleTableSpacing = (component: Component, property: string, doUpdateMSOPadding = false): void => {
    if (editor.runCommand(CMDS.IS_OF_TYPE_TABLE, {component}) || editor.runCommand(CMDS.IS_OF_TYPE_CELL, {component})) {
      const styles = component.getStyle();

      if (typeof styles[property] !== 'undefined') {
        if (doUpdateMSOPadding) {
          component.addStyle({'mso-padding-alt': styles[property]});
        }

        component.addAttributes({
          cellpadding: parseInt(styles[property] as string, 10),
        });
      }
    }
  };

  /**
   *
   * @param {string} hashedFilename
   * @param {string} type
   * @returns {Promise<*>}
   */
  const checkEmailMediaPresetsStatus = (hashedFilename: string, type: string): Promise<void> => {
    return window.PMW.readLocal('emailmaker/checkEmailMediaPresetStatus', {
      hashedFilename,
      type,
    }) as Promise<void>;
  };

  cmds.add(
    CMDS.ADD_COMPONENT_ANCHOR,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        url: string;
        component: Component;
        shouldOpenInNewTab?: boolean;
        customLinkComponent?: string;
      }
    ) => {
      if (typeof opts.component !== 'undefined' && typeof opts.url === 'string') {
        const {component} = opts;
        let linkComponent = opts.customLinkComponent;

        if (typeof linkComponent !== 'string') {
          linkComponent = window.PMW.emails.components.text.PMW_LINK;
        }

        const shouldOpenInNewTab = opts.shouldOpenInNewTab ?? true;

        component.addAttributes({
          'data-url': opts.url,
          'has-custom-url': 1,
        });

        // Tracking temporarily disabled on 8th Nov 2023
        // editor.runCommand(CMDS.TRACK_GA_EVENT_COMPONENT, {
        //     component: component,
        //     eventAction: 'EmailMaker_AddLink'
        // });

        if (component.is(linkComponent) || component.is(window.PMW.emails.components.text.PMW_BUTTON)) {
          component.addAttributes({
            href: opts.url,
            ...(shouldOpenInNewTab ? {target: '_blank'} : {}),
          });
        } else {
          let anchor;
          const parent = component.parent();

          if (parent && parent.get('type') === linkComponent) {
            anchor = parent;
            anchor.setAttributes({
              'data-url': opts.url,
              href: opts.url,
              'has-custom-url': 1,
            });
          } else if (parent) {
            anchor = parent.append(
              {
                type: linkComponent,
                tagName: 'a',
                attributes: {
                  'data-url': opts.url,
                  href: opts.url,
                  'has-custom-url': 1,
                },
              },
              {at: component.index()}
            )[0];
            component.move(anchor);
          }

          if (shouldOpenInNewTab && anchor) {
            anchor.addAttributes({
              target: '_blank',
            });
          }

          if (anchor) {
            component.set(window.PMW.emails.props.PARENT_ANCHOR_COMPONENT_ID, anchor.getId());

            editor.Canvas.scrollTo(anchor, {
              force: true,
              behavior: 'smooth',
            });
          }

          editor.select(component);
        }
      }
    }
  );

  cmds.add(CMDS.IS_OF_TYPE_CONTAINER, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): boolean => {
    const component = opts.component || editor.getSelected();
    if (!component) {
      return false;
    }

    const type = component.get('type');

    return (
      type === window.PMW.emails.components.container.PMW_CONTENT ||
      type === window.PMW.emails.components.container.PMW_WRAPPER ||
      type === window.PMW.emails.components.container.PMW_WRAPPER_CELL
    );
  });

  cmds.add(CMDS.GET_ALL_COMPONENTS_WITH_FONT, (editorInstance: Editor): Component[] => {
    const wrapper = editorInstance.getWrapper()?.findType(window.PMW.formmaker.components.structural.PMW_FORMS_WRAPPER);

    if (!wrapper) {
      return [];
    }

    const forms = wrapper[0].components();
    let components: Component[] = [];

    forms.models.map((form: Component): void => {
      components = [...components, ...getAllChildrenComponentsWithFont(form)];
    });

    return components;
  });

  function getAllChildrenComponentsWithFont(selectedComponent: Component): Component[] {
    let components: Component[] = [];

    const checkComponentFont = (component: Component): Component[] => {
      const isEditable = component.get('editable');
      const componentFont = component.getStyle()['font-family'];

      if (isEditable !== false && componentFont !== undefined) {
        components = [...components, component];
      }

      const children = component.get('components');
      if (children) {
        for (let i = 0; i < children.length; i++) {
          const child = children.models[i];
          if (checkComponentFont(child) === null) {
            return [];
          }
        }
      }

      return components;
    };

    return checkComponentFont(selectedComponent);
  }

  const pasteComponentToWindow = (selected: Component, index: number): void => {
    const components = JSON.parse(window.localStorage.getItem(window.PMW.emails.EMAIL_DATA_CLIPBOARD_KEY)!) as Component[];
    const contentContainer = editor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_CONTENT_CONTAINER)[0];
    const indexToAppendComponentTo = index + 1;
    let addedComponent;

    if (components) {
      if (selected) {
        const isValid = editor.Components.canMove(selected, components as unknown as Component, indexToAppendComponentTo).result;

        if (isValid) {
          addedComponent = selected.append(components, {at: indexToAppendComponentTo});
          editor.trigger('component:paste', addedComponent);
          selected.emitUpdate();
          return;
        }

        if (!isValid || editor.runCommand(CMDS.IS_OF_TYPE_CONTAINER, {component: selected})) {
          const parent = selected.parent();

          if (parent && parent.get('type') !== window.PMW.emails.components.container.PMW_CONTENT_CONTAINER) {
            pasteComponentToWindow(parent, index);
            return;
          }

          if (contentContainer) {
            addedComponent = contentContainer.append(components, {at: indexToAppendComponentTo});
            editor.trigger('component:paste', addedComponent);
            contentContainer.emitUpdate();
          }
        }
      } else if (contentContainer) {
        addedComponent = contentContainer.append(components as unknown as string, {at: 0});
        editor.trigger('component:paste', addedComponent);
      }

      if (addedComponent) {
        editor.runCommand(window.PMW.emails.commands.SANITIZE_COMPONENT_CONTENT, {component: addedComponent});
      }
    }
  };

  /**
   * This is here to override the default paste command for grapesjs.
   * Please do not remove.
   * */
  cmds.add('core:paste', () => {});

  cmds.add(CMDS.CORE_COMPONENT_PASTE, () => {
    const selected = editor.getSelected();

    if (!selected) {
      return;
    }

    const index = selected.index();
    pasteComponentToWindow(selected, index);
  });

  cmds.add(CMDS.CORE_COMPONENT_COPY, () => {
    const comps = editor.getSelectedAll();

    if (comps.length > 0) {
      const validComps = comps.filter((comp) => !comp.is(window.PMW.emails.components.table.PMW_CELL));

      if (validComps.length > 0) {
        const serializedComps = validComps.map((comp) => comp.toJSON());
        window.localStorage.setItem(window.PMW.emails.EMAIL_DATA_CLIPBOARD_KEY, JSON.stringify(serializedComps));
      }
    }
  });

  cmds.add(CMDS.VALIDATE_ALL_TBODY_COMPONENTS, (editorInstance: Editor, _sender: object, _opts: BasicComponentCommandProps) => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component, []);

    for (const component of components) {
      editor.runCommand(CMDS.VALIDATE_TBODY_COMPONENT_TYPES, {component});
    }
  });

  cmds.add(CMDS.VALIDATE_TBODY_COMPONENT_TYPES, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const {component} = opts;

    if (!component) {
      return;
    }

    if ((component.is(window.PMW.emails.components.default.DEFAULT) || !component.get('type')) && component.get('tagName')?.toLowerCase() === 'tbody') {
      component.set({
        type: window.PMW.emails.components.table.PMW_TBODY,
        attributes: {
          ...component.get('attributes'),
          'data-gjs-type': window.PMW.emails.components.table.PMW_TBODY,
          'data-is-pmw-tbody': '1',
          'data-pmw-type': window.PMW.emails.components.table.PMW_TBODY,
        },
      });
    }
  });

  cmds.add(CMDS.CUT_COMPONENT, (_editorInstance: Editor, sender: object, opts: BasicComponentCommandProps) => {
    editor.runCommand(CMDS.CORE_COMPONENT_COPY);
    editor.runCommand(CMDS.REMOVE_COMPONENT, {editor, sender, opts});
  });

  cmds.add(CMDS.SANITIZE_ALL_COMPONENTS, (editorInstance: Editor) => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component, []);

    for (const component of components) {
      editor.runCommand(CMDS.SANITIZE_COMPONENT_CONTENT, {component});
    }
  });

  cmds.add(CMDS.SET_TARGET_BLANK_FOR_ALL_LINKS, (editorInstance: Editor): void => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component, []);

    for (const component of components) {
      if (editorInstance.runCommand(CMDS.DOES_COMPONENT_HAVE_LINK, {component}) || component.get('tagName')?.toLowerCase() === 'a') {
        if (component.getAttributes().target !== '_blank') {
          component.addAttributes({target: '_blank'}, {silent: true});
        }
      }
    }
  });

  cmds.add(CMDS.SANITIZE_COMPONENT_CONTENT, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): void => {
    const {component} = opts;

    function sanitizeComponent(comp: Component): void {
      if (!comp || typeof comp.get !== 'function') {
        return;
      }

      const content = comp.get('content');
      if (typeof content === 'string') {
        const cleanedContent = xssCleanHtml(content);
        if (cleanedContent !== content) {
          comp.set('content', cleanedContent);
        }
      }

      const attributes = comp.get('attributes') || {};
      const harmfulAttributes = Object.keys(attributes).filter((attr) => {
        return attr.toLowerCase().startsWith('on') || attr.toLowerCase() === 'srcdoc';
      });
      harmfulAttributes.forEach((attr) => comp.removeAttributes(attr));

      const tagName = comp.get('tagName');
      const disallowedTags = [
        'script',
        'iframe',
        'object',
        'embed',
        'form',
        'link',
        'style',
        'meta',
        'base',
        'frame',
        'frameset',
        'applet',
        'canvas',
        'noscript',
        'svg',
        'audio',
        'video',
        'source',
        'track',
      ];

      if (tagName && disallowedTags.includes(tagName.toLowerCase())) {
        comp.remove();
        return;
      }

      const children = comp.components();
      if (children?.length) {
        children.forEach(sanitizeComponent);
      }
    }

    if (component) {
      sanitizeComponent(component);
    }
  });

  cmds.add(CMDS.DOES_COMPONENT_HAVE_LINK, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): boolean => {
    const {component} = opts;
    if (!component) {
      return false;
    }

    return (
      component.is(window.PMW.emails.components.social.PMW_SOCIALS_ITEM) ||
      component.is(window.PMW.emails.components.text.PMW_LINK) ||
      component.is(window.PMW.emails.components.text.PMW_BUTTON)
    );
  });

  cmds.add(CMDS.UPDATE_MS_OUTLOOK_STYLES, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): void => {
    if (typeof opts.component !== 'undefined') {
      const {component} = opts;

      if (component && editor.runCommand(CMDS.HAS_WIDTH_PROPERTY, {component})) {
        if (!component.view) {
          return;
        }

        const width = getNormalizedWidth(component.view.el.offsetWidth);

        if (width) {
          component.removeAttributes(['width', 'height'], {silent: true});
          component.addAttributes(
            {
              width,
            },
            {
              silent: true,
            }
          );
        }
      }
    }

    function getNormalizedWidth(elementWidth: number): number {
      let documentWidth = $('#html-editor-canvas').get(0)?.offsetWidth;

      if (!documentWidth) {
        documentWidth = 1;
      }

      const MAX_MAILBOX_WIDTH = 700;

      return Math.round((elementWidth / documentWidth) * MAX_MAILBOX_WIDTH);
    }
  });

  cmds.add(
    CMDS.IS_COMPONENT_INSIDE_COMPONENT,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        child: Component;
        parent: Component;
      }
    ) => {
      const {parent} = opts;
      let innerComponent: undefined | Component = opts.child;

      while (innerComponent) {
        if (innerComponent === parent) {
          return true;
        }

        if (innerComponent.get('type') === window.PMW.emails.components.table.PMW_SECTION) {
          return false;
        }

        innerComponent = innerComponent.parent();
      }

      return false;
    }
  );

  cmds.add(CMDS.HAS_WIDTH_PROPERTY, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    if (typeof opts.component !== 'undefined') {
      const type = opts.component.get('type');

      return (
        type === window.PMW.emails.components.graphic.PMW_IMAGE ||
        type === window.PMW.emails.components.graphic.PMW_DESIGN ||
        type === window.PMW.emails.components.graphic.PMW_STICKER ||
        type === window.PMW.emails.components.graphic.PMW_VIDEO ||
        type === window.PMW.emails.components.social.PMW_SOCIALS_IMAGE ||
        type === window.PMW.emails.components.table.PMW_TABLE ||
        type === window.PMW.emails.components.table.PMW_SECTION ||
        type === window.PMW.emails.components.table.PMW_CELL ||
        type === window.PMW.emails.components.text.PMW_BUTTON
      );
    }

    return false;
  });

  cmds.add(CMDS.REMOVE_COMPONENT_ANCHOR, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    if (typeof opts.component !== 'undefined') {
      const {component} = opts;
      const anchor = component.parent();

      if (anchor?.get('tagName')?.toLowerCase() === 'a') {
        if (anchor.parent() !== undefined) {
          component.move(anchor.parent()!, {at: anchor.index()});
        }
        anchor.remove();
        component.removeAttributes(['href']);
        component.set(window.PMW.emails.props.PARENT_ANCHOR_COMPONENT_ID, undefined);
        editor.select(component);
      }
    }
  });

  cmds.add(
    CMDS.GET_COMPONENT_BY_LOAD_IDENTIFIER,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        identifier: string;
      }
    ) => {
      const {identifier} = opts;

      const wrapper = editor.DomComponents.getWrapper();
      return wrapper?.find(`[${window.PMW.emails.props.DROP_IDENTIFIER}="${identifier}"]`)[0];
    }
  );

  cmds.add(CMDS.UPDATE_LOADING_IMG_WITH_RESPONSE, (_editorInstance: Editor, _sender: object, opts: EmailUploadResponseProps) => {
    const {identifier} = opts;
    const img = opts.imgResponse;

    if (identifier) {
      const componentToLoadOn = editor.runCommand(CMDS.GET_COMPONENT_BY_LOAD_IDENTIFIER, {
        identifier,
      }) as Component;

      if (componentToLoadOn) {
        editor.runCommand(CMDS.SET_IMAGE_SRC, {
          component: componentToLoadOn,
          source: img.src,
          extension: img.ext,
          hashedFilename: img.filename,
        });
      }
    }
  });

  cmds.add(CMDS.GET_STORAGE_DATA_WITHOUT_FOOTER, (_editorInstance: Editor, _sender: object, opts: CustomStorageData): any => {
    const storageData = opts.data;

    if (
      storageData.length > 0 &&
      typeof storageData[0].frames !== 'undefined' &&
      storageData[0].frames[0] !== 'undefined' &&
      storageData[0].frames[0].component !== 'undefined' &&
      storageData[0].frames[0].component.components !== 'undefined' &&
      storageData[0].frames[0].component.components !== 'undefined'
    ) {
      const {components} = storageData[0].frames[0].component;
      const lastComponent = components[components.length - 1];

      if (lastComponent.type === window.PMW.emails.components.misc.PMW_READ_ONLY_FOOTER) {
        components.pop();
      }
    }

    return storageData;
  });

  cmds.add(CMDS.REMOVE_UNSUPPORTED_STYLES, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): void => {
    const {component} = opts;

    if (!component) {
      return;
    }

    const styles = component.getStyle();
    const unstylable = component.get('unstylable') as string[] ?? UNSUPPORTED_EMAIL_STYLES;

    if (!unstylable) {
      return;
    }

    unstylable.forEach((property) => {
      if (styles[property]) {
        component.removeStyle(property);
      }
    });
  });

  cmds.add(CMDS.ADD_READONLY_FOOTER, async (): Promise<void> => {
    const footer = editor.DomComponents.getWrapper()?.find(`#${window.PMW.emails.READ_ONLY_FOOTER_ID}`);

    if (!footer || footer.length > 0) {
      return;
    }

    const r = (await window.PMW.readLocal('emailmaker/getEmailTemplateFooterHTML')) as {html: string};

    editor.addComponents(
      {
        type: window.PMW.emails.components.misc.PMW_READ_ONLY_FOOTER,
        attributes: {
          id: window.PMW.emails.READ_ONLY_FOOTER_ID,
          'data-is-readonly-text': true,
        },
        style: {
          display: 'block',
          'text-align': ALIGNMENT_CENTER,
        },
        content: r.html,
      },
      {
        // eslint-disable-next-line
        // @ts-ignore
        avoidStore: true,
        noCount: true,
      }
    );
  });

  cmds.add(CMDS.REMOVE_READONLY_FOOTER, async (): Promise<void> => {
    const wrapper = editor.DomComponents.getWrapper();

    if (wrapper) {
      const footer = wrapper.find(`#${window.PMW.emails.READ_ONLY_FOOTER_ID}`);

      if (footer.length > 0) {
        /**
         * @var {grapesjs.Component} item
         */
        footer.forEach((item) => {
          item.remove();
        });
      }
    }
  });

  cmds.add(CMDS.REATTACH_COMPONENT_ANCHOR, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    if (typeof opts.component !== 'undefined') {
      const {component} = opts;
      const componentEl = component.getEl();
      if (componentEl && window.PMW.isDOMElement(componentEl) && componentEl.getAttribute('has-custom-url')) {
        const url = componentEl.getAttribute('data-url');

        editor.runCommand(CMDS.REMOVE_COMPONENT_ANCHOR, {
          component,
        });

        editor.runCommand(CMDS.ADD_COMPONENT_ANCHOR, {
          component,
          url,
        });
      }
    }
  });

  cmds.add(CMDS.MOVE_COMPONENT_ANCHOR, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const {component} = opts;
    if (!component) {
      return;
    }

    const anchorID = component.get(window.PMW.emails.props.PARENT_ANCHOR_COMPONENT_ID) as string;
    if (!anchorID) {
      return;
    }

    const anchor = anchorID ? editor.getWrapper()?.find(`#${anchorID}`)[0] : component.parent();

    if (anchor && anchor.get('tagName')?.toLowerCase() === 'a') {
      anchor.move(component.parent()!, {at: component.index()});
      component.move(anchor);
    }
  });

  cmds.add(CMDS.V_ALIGN_COMPONENT_TOP, () => {
    const component = editor.getSelected();
    if (component) {
      const styles = component.getStyle();
      styles['vertical-align'] = 'top';
      component.setStyle(styles);
      editor.trigger('component:styleUpdate:vertical-align');
    }
  });

  cmds.add(CMDS.V_ALIGN_COMPONENT_MIDDLE, () => {
    const component = editor.getSelected();

    if (component) {
      const styles = component.getStyle();

      styles['vertical-align'] = 'middle';
      component.setStyle(styles);

      editor.trigger('component:styleUpdate:vertical-align');
    }
  });

  cmds.add(CMDS.V_ALIGN_COMPONENT_BOTTOM, () => {
    const component = editor.getSelected();
    if (component) {
      const styles = component.getStyle();

      styles['vertical-align'] = 'bottom';
      component.setStyle(styles);
      editor.trigger('component:styleUpdate:vertical-align');
    }
  });

  cmds.add(CMDS.H_ALIGN_COMPONENT_CENTER, () => {
    const component = editor.getSelected();
    if (component) {
      const styles = component.getStyle();

      styles['margin-left'] = 'auto';
      styles['margin-right'] = 'auto';
      component.setStyle(styles);
      editor.runCommand(CMDS.ADD_ALIGN_ATTRIBUTE, {
        component,
        alignment: ALIGNMENT_CENTER,
      });
      editor.trigger('component:styleUpdate:margin');
    }
  });

  cmds.add(CMDS.H_ALIGN_COMPONENT_LEFT, () => {
    const component = editor.getSelected();

    if (component) {
      const styles = component.getStyle();

      styles['margin-left'] = '0';
      styles['margin-right'] = 'auto';
      component.setStyle(styles);
      editor.runCommand(CMDS.ADD_ALIGN_ATTRIBUTE, {
        component,
        alignment: ALIGNMENT_LEFT,
      });
      editor.trigger('component:styleUpdate:margin');
    }
  });

  cmds.add(CMDS.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const component = opts.component || editor.getSelected();
    if (!component) {
      return;
    }

    const styles = component.getStyle();

    if (styles) {
      if (isCenterAligned(styles)) {
        editor.runCommand(CMDS.ADD_ALIGN_ATTRIBUTE, {
          component,
          alignment: ALIGNMENT_CENTER,
        });
      }

      if (isLeftAligned(styles)) {
        editor.runCommand(CMDS.ADD_ALIGN_ATTRIBUTE, {
          component,
          alignment: ALIGNMENT_LEFT,
        });
      }

      if (isRightAligned(styles)) {
        editor.runCommand(CMDS.ADD_ALIGN_ATTRIBUTE, {
          component,
          alignment: ALIGNMENT_RIGHT,
        });
      }
    }
  });

  cmds.add(CMDS.GET_COMPONENT_HORIZONTAL_ALIGNMENT, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): ComponentAlignmentStyle | undefined => {
    const component = opts.component ?? editor.getSelected();
    if (!component?.getStyle()) {
      return undefined;
    }

    const elementStyles = component?.getEl()?.style;
    const parentAlignment = (component.parent()?.getAttributes().align as ComponentAlignmentStyle) ?? '';

    if (elementStyles?.marginLeft === 'auto' && elementStyles?.marginRight === 'auto') {
      return 'center';
    }
    if (elementStyles?.marginRight === 'auto' && parseInt(elementStyles.marginLeft) === 0) {
      return 'left';
    }
    if (elementStyles?.marginLeft === 'auto' && parseInt(elementStyles.marginRight) === 0) {
      return 'right';
    }

    switch (parentAlignment) {
      case 'center':
      case 'middle':
        return 'center';
      case 'left':
        return 'left';
      case 'right':
        return 'right';
      default:
        return undefined;
    }
  });

  cmds.add(CMDS.H_ALIGN_COMPONENT_RIGHT, () => {
    const component = editor.getSelected();

    if (!component) {
      return;
    }

    const styles = component.getStyle();

    styles['margin-left'] = 'auto';
    styles['margin-right'] = '0';
    component.setStyle(styles);
    editor.runCommand(CMDS.ADD_ALIGN_ATTRIBUTE, {
      component,
      alignment: ALIGNMENT_RIGHT,
    });
    editor.trigger('component:styleUpdate:margin');
  });

  cmds.add(CMDS.GET_COMPONENT_STYLE, {
    run(_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps & {property: string}) {
      const component = opts.component || editor.getSelected();
      if (!component) {
        return null;
      }

      const componentStyles = component.getStyle();
      const value = componentStyles[opts.property];

      if (typeof value === 'undefined') {
        return null;
      }

      return value;
    },
  });

  /**
   * @param {string} opts.property
   * @param {string|number} opts.value
   * @param {grapesjs.Component} [opts.component] if not provided, selected component will be used
   */
  cmds.add(CMDS.SET_COMPONENT_STYLE, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps & StyleSetterProps) => {
    const component = opts.component || editor.getSelected();

    if (!component) {
      return;
    }

    if (typeof opts.property !== 'undefined' && typeof opts.value !== 'undefined') {
      editor.runCommand(CMDS.UPDATE_DIVIDER_COMPATIBILITY_PROPERTY, {
        component,
        property: opts.property,
        value: opts.value,
      });

      if (opts.property && opts.property === window.PMW.emails.styles.LINK_COLOR) {
        const attributesToAdd: StyleAttributes = {};
        attributesToAdd[window.PMW.emails.styles.LINK_COLOR] = opts.value;
        component.addAttributes(attributesToAdd);
      }

      const style: StyleAttributes = {};
      style[opts.property] = opts.value;
      component.addStyle(style);
      editor.trigger(`component:styleUpdate:${opts.property}`, component);
    }
  });

  cmds.add(CMDS.SET_TEXT_COLOR_STYLE, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps & StyleSetterProps) => {
    const component = opts.component ?? editor.getSelected();

    if (!component || opts.property !== 'color') {
      return;
    }

    removeRTEColorpickerFontTagsIfNeeded(component);

    const style: StyleAttributes = {[opts.property]: opts.value};
    component.addStyle(style);
    editor.trigger(`component:styleUpdate:${opts.property}`, component);
  });

  const removeRTEColorpickerFontTagsIfNeeded = (component: Component): void => {
    const el = component.getEl();

    if (!el) {
      return;
    }

    const elCopy = document.createElement('div');
    elCopy.innerHTML = el.innerHTML;

    const fontTags: NodeListOf<HTMLElement> = elCopy.querySelectorAll('font');

    if (fontTags.length === 0) {
      return;
    }

    fontTags.forEach((fontTag) => {
      while (fontTag.firstChild) {
        fontTag.parentNode?.insertBefore(fontTag.firstChild, fontTag);
      }

      fontTag.remove();
    });

    component.components(elCopy.innerHTML);
  };

  cmds.add(
    CMDS.ADD_ALIGN_ATTRIBUTE,
    (
      editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        alignment: string;
      }
    ) => {
      const component = opts.component || editor.getSelected();
      if (!component) {
        return;
      }

      if (
        editorInstance.runCommand(CMDS.IS_OF_TYPE_GRAPHIC, {
          component,
        })
      ) {
        const parent = component.parent();

        if (
          parent &&
          editor.runCommand(CMDS.IS_OF_TYPE_CELL, {
            component: parent,
          })
        ) {
          parent.addAttributes({
            align: opts.alignment,
          });
        }
      }
    }
  );

  cmds.add(CMDS.IS_OF_TYPE_GRAPHIC, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const component = opts.component || editor.getSelected();
    if (!component) {
      return false;
    }

    const type = component.get('type');

    return (
      type === window.PMW.emails.components.graphic.PMW_VIDEO ||
      type === window.PMW.emails.components.graphic.PMW_IMAGE ||
      type === window.PMW.emails.components.graphic.PMW_STICKER ||
      type === window.PMW.emails.components.graphic.PMW_DESIGN
    );
  });

  cmds.add(CMDS.UPDATE_DIVIDER_COMPATIBILITY_PROPERTY, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps & StyleSetterProps) => {
    const component = opts.component || editor.getSelected();
    if (component && component.get('type') === window.PMW.emails.components.misc.PMW_DIVIDER) {
      const dividerCell = component.findType(window.PMW.emails.components.misc.PMW_DIVIDER_CELL);

      if (dividerCell.length > 0) {
        const style: StyleAttributes = {};
        style[opts.property] = opts.value;
        dividerCell[0].addStyle(style);
        editor.trigger(`component:styleUpdate:${+opts.property}`, dividerCell[0]);
      }
    }
  });

  cmds.add(CMDS.DELETE_REDUNDANT_COMPONENTS, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const {component} = opts;
    if (component) {
      if (REDUNDANT_COMPONENTS.includes(component.get('type')!)) {
        component.remove({silent: true});
      }
    }
  });

  /**
   * Set a media query for a custom property value for either tablet or mobile
   * @param {object} opts
   * @param {string} opts.id component id to use for the query
   * @param {string} opts.property
   * @param {string|number} opts.value
   * @param {string} opts.cssPrefix
   * @param {string} opts.deviceID
   */
  cmds.add(
    CMDS.SET_DEVICE_MEDIA_QUERY,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: StyleSetterProps & {
        id: string;
        cssPrefix: string;
        deviceID: string;
      }
    ) => {
      const css = editor.Css;
      const componentID = opts.id;
      const device = editor.Devices.get(opts.deviceID);
      if (!device) {
        return;
      }

      const maxWidth = device.getWidthMedia();
      // eslint-disable-next-line
      // @ts-ignore
      const minWidth = device.get('minWidthMedia');
      const property = opts.property.replace(opts.cssPrefix, '');
      const atMediaRules = `(max-width: ${maxWidth})${minWidth ? ` and (min-width: ${minWidth})` : ''}`;

      css.setRule(
        `#${componentID}`,
        {
          [property]: `${opts.value} !important`,
        },
        {
          atRuleType: 'media',
          atRuleParams: `screen and ${atMediaRules}`,
        }
      );
    }
  );

  cmds.add(
    CMDS.TOGGLE_COMPONENT_STYLE,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        style: string;
        default: string;
        active: string;
      }
    ) => {
      const component = editor.getSelected();

      if (!component) {
        return;
      }

      const styleToToggle = opts.style;
      const {active} = opts;
      const dflt = opts.default;

      if (component && typeof component.getStyle()[styleToToggle] === 'string') {
        if (component.getStyle()[styleToToggle] === active) {
          editor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: styleToToggle, value: dflt});
        } else {
          editor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: styleToToggle, value: active});
        }
      } else {
        editor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: styleToToggle, value: active});
      }
    }
  );

  cmds.add(CMDS.TOGGLE_STYLE_BOLD, () => {
    editor.runCommand(CMDS.TOGGLE_COMPONENT_STYLE, {
      style: 'font-weight',
      active: 'bold',
      default: 'normal',
    });
  });

  cmds.add(CMDS.TOGGLE_STYLE_ITALIC, () => {
    editor.runCommand(CMDS.TOGGLE_COMPONENT_STYLE, {
      style: 'font-style',
      active: 'italic',
      default: 'normal',
    });
  });

  const toggleTextDecorationStyle = (_editorInstance: Editor, styleToToggle: string): void => {
    const selected = editor.getSelected();

    if (!selected) {
      return;
    }

    if (!(selected.is(window.PMW.emails.components.text.PMW_TEXT) || selected.is(window.PMW.emails.components.text.PMW_BUTTON))) {
      return;
    }

    const styles = selected.getStyle();
    const textDecoration = (styles['text-decoration'] || '') as string;
    const currentStyles = textDecoration.split(' ');

    const newStyles = currentStyles.filter((style) => {
      return style !== styleToToggle && style !== 'none';
    });

    if (!currentStyles.includes(styleToToggle)) {
      newStyles.push(styleToToggle);
    }

    if (newStyles.length === 0) {
      newStyles.push('none');
    }

    const newValue = newStyles.join(' ');

    editor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: 'text-decoration', value: newValue});
  };

  cmds.add(CMDS.TOGGLE_STYLE_UNDERLINE, (editorInstance: Editor) => {
    toggleTextDecorationStyle(editorInstance, 'underline');
  });

  cmds.add(CMDS.TOGGLE_STYLE_STRIKETHROUGH, (editorInstance: Editor) => {
    toggleTextDecorationStyle(editorInstance, 'line-through');
  });

  cmds.add(CMDS.IS_OF_TYPE_TABLE, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    let {component} = opts;
    component = component || editor.getSelected();

    if (!component) {
      return false;
    }

    return doesComponentHaveTagName(component, 'TABLE');
  });

  cmds.add(CMDS.IS_OF_TYPE_CELL, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): boolean => {
    let {component} = opts;
    component = component || editor.getSelected();

    if (!component) {
      return false;
    }

    return doesComponentHaveTagName(component, 'TD');
  });

  cmds.add(CMDS.UPDATE_CONTENT_MAX_WIDTH, (_editorInstance: Editor, _sender: object, options: BasicComponentCommandProps) => {
    let {component} = options;
    if (!component) {
      return;
    }

    component = component || editor.getSelected();

    editor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: 'max-width', value: window.PMW.emails.MAX_CONTENT_WIDTH, component});
  });

  cmds.add(CMDS.HANDLE_TABLE_PADDING, (_editorInstance: Editor, _sender: object, options: BasicComponentCommandProps) => {
    let {component} = options;
    if (!component) {
      return;
    }

    component = component || editor.getSelected();

    handleTableSpacing(component, 'padding', true);
  });

  const doesTableHaveRadiusProperty = (tableStyles: StyleProps, radiusProperty: string) => {
    return typeof tableStyles[radiusProperty] === 'string' && tableStyles[radiusProperty] !== '0px' && tableStyles[radiusProperty] !== '0';
  };

  cmds.add(CMDS.DOES_TABLE_HAVE_ROUNDNESS, (_editorInstance: Editor, _sender: object, options: BasicComponentCommandProps): boolean => {
    const {component} = options;
    if (component) {
      const tableStyles = component.getStyle();

      if (
        doesTableHaveRadiusProperty(tableStyles, 'border-radius') ||
        doesTableHaveRadiusProperty(tableStyles, 'border-top-right-radius') ||
        doesTableHaveRadiusProperty(tableStyles, 'border-top-left-radius') ||
        doesTableHaveRadiusProperty(tableStyles, 'border-bottom-right-radius') ||
        doesTableHaveRadiusProperty(tableStyles, 'border-bottom-left-radius')
      ) {
        return true;
      }
    }

    return false;
  });

  cmds.add(CMDS.SYNC_TABLE_TBODY_COLORS, (_editorInstance: Editor, _sender: object, options: BasicComponentCommandProps) => {
    const {component} = options;

    if (!component) {
      return;
    }

    const tableBody = component.find(window.PMW.emails.components.default.TBODY)[0];

    if (!tableBody) {
      return;
    }

    const tableStyles = component.getStyle();
    let colorToApply = 'transparent';

    if (typeof tableStyles['background-color'] !== 'undefined') {
      colorToApply = tableStyles['background-color'] as string;
    }

    if (editor.runCommand(CMDS.DOES_TABLE_HAVE_ROUNDNESS, {component})) {
      colorToApply = 'transparent';
    }

    const tbodyStyles: Record<string, string> = {};
    tbodyStyles['background-color'] = colorToApply;

    tableBody.setStyle(tbodyStyles);
  });

  cmds.add(CMDS.HANDLE_TABLE_MARGIN, (_editorInstance: Editor, _sender: object, options: BasicComponentCommandProps) => {
    let {component} = options;

    if (!component) {
      return;
    }

    component = component || editor.getSelected();

    handleTableSpacing(component, 'margin');
  });

  cmds.add(CMDS.HIGHLIGHT_COMPONENT_PADDING, (_editorInstance: Editor, _sender: object, options: BasicComponentCommandProps) => {
    if (shouldPreventElementHighlightingOnUpdate()) {
      return;
    }

    if (window.PMW.isInitializingEditor) {
      return;
    }

    let {component} = options;

    if (!component) {
      return;
    }

    component = component || editor.getSelected();

    if (!component) {
      return;
    }

    const el = component.getEl();
    const backgroundColor = getComponentBgColor(component);
    const backgroundImage = getComponentBgImage(component);

    if (!el) {
      return;
    }

    el.style.backgroundImage = `linear-gradient(to bottom, ${backgroundColor}, ${backgroundColor}), linear-gradient(to bottom, #9BECD980, #9BECD980)`;
    el.style.backgroundClip = 'content-box, padding-box';
    editor.refresh();

    window.PMW.debounce(
      setTimeout(() => {
        if (!isSliderActive()) {
          el.style.backgroundImage = backgroundImage;
          el.style.backgroundClip = 'padding-box';
        }
      }, 100),
      100
    );
  });

  function shouldPreventElementHighlightingOnUpdate(): boolean {
    const puppeteerConfig = window.PMW.emails.PUPPETEER_CONFIG;
    return !!puppeteerConfig;
  }

  /**
   * @param {object} opts
   * @param {HTMLElement} opts.el
   */
  cmds.add(CMDS.HIGHLIGHT_CELL_SPACING, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    if (shouldPreventElementHighlightingOnUpdate()) {
      return;
    }

    if (window.PMW.isInitializingEditor) {
      return;
    }

    const component = opts.component;

    if (!component) {
      return;
    }

    const backgroundColor = getComponentBgColor(component);
    const backgroundClip = getComponentBgClip(component);
    const backgroundImage = getComponentBgImage(component);

    const el = component.getEl();

    if (!el) {
      return;
    }

    el.style.backgroundImage = `linear-gradient(to bottom, #9BECD980, #9BECD980), linear-gradient(to bottom, ${backgroundColor}, ${backgroundColor}`;
    el.style.backgroundClip = 'content-box, padding-box';
    editor.refresh();

    window.PMW.debounce(
      setTimeout(() => {
        if (!isSliderActive()) {
          el.style.backgroundImage = backgroundImage;
          el.style.backgroundClip = backgroundClip;
        }
      }, 100),
      100
    );
  });

  cmds.add(CMDS.ADD_CONTENT_COMPONENT, () => {
    const children = editor.getComponents();

    editor.getWrapper()?.set({
      droppable: false,
      stylable: false,
      draggable: false,
      highlightable: false,
      selectable: false,
    });

    if (children && children.models.length === 0) {
      placeMainContent();
    } else {
      backwardsCompatibilityForDivs();
      const mainWrapper = editor.runCommand(CMDS.GET_MAIN_WRAPPER) as Component;
      editor.select(mainWrapper, {
        // eslint-disable-next-line
        // @ts-ignore
        avoidStore: true,
        noCount: true,
      });
    }
  });

  /**
   * set postermywall-wrapper as default selection
   * @param {grapesjs.Component} opts.component
   */

  cmds.add(CMDS.REMOVE_COMPONENT, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    editor.getSelectedAll().forEach((component) => {
      opts.component = opts.component ?? component;
      editor.runCommand(CMDS.REMOVE_COMPONENT_ANCHOR, {component});
      editor.runCommand('core:component-delete', {component});
      editor.runCommand(CMDS.SET_DEFAULT_SELECTION, {});

      // Tracking temporarily disabled on 8th Nov 2023
      // editor.runCommand(CMDS.TRACK_GA_EVENT_COMPONENT, {
      //     component: component,
      //     eventAction: 'EmailMaker_Delete'
      // });
    });
  });

  cmds.add(
    CMDS.TRACK_GA_EVENT_COMPONENT,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        eventValue: string;
        eventAction: string;
      }
    ) => {
      /**
       * @type {grapesjs.Component}
       */
      opts.component = opts.component || editor.getSelected();

      window.PMW.gtm.trackGA4CustomEvent(opts.eventAction, {[GA4EventParamName.TYPE]: opts.component?.get('trackingName')});
    }
  );

  cmds.add(CMDS.UPDATE_SOCIAL_LINK_SPACING, (_editorInstance: Editor, _sender: object, opts: {gap: number}) => {
    const selected = editor.getSelected();

    if (selected?.is(window.PMW.emails.components.social.PMW_SOCIALS)) {
      const {gap} = opts;
      let style;

      selected.get('components')?.each((socialLink) => {
        style = socialLink.getStyle();
        style['margin-left'] = `${gap}px`;
        style['margin-right'] = `${gap}px`;
        socialLink.setStyle(style);
      });
    }
  });

  cmds.add(CMDS.UPDATE_SOCIAL_LINK_SIZE, (_editorInstance: Editor, _sender: object, opts: {size: number}) => {
    const selected = editor.getSelected();

    if (selected?.is(window.PMW.emails.components.social.PMW_SOCIALS)) {
      const {size} = opts;
      let style;

      selected.get('components')?.each((socialLink) => {
        socialLink.get('components')?.each((socialImage) => {
          style = socialImage.getStyle();
          style.width = `${size}px`;
          style.height = `${size}px`;
          socialImage.setStyle(style);
        });
      });
    }
  });

  cmds.add(CMDS.UPDATE_SOCIAL_LINK_ICON_STYLE, (_editorInstance: Editor, _sender: object, opts: {iconStyle: string}) => {
    const selected = editor.getSelected();
    const {iconStyle} = opts;

    editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
      component: selected,
      property: 'icon-style',
      value: iconStyle,
    });

    if (selected?.is(window.PMW.emails.components.social.PMW_SOCIALS)) {
      selected.get('components')?.each((socialLink) => {
        socialLink.get('components')?.each((socialImage) => {
          if (socialImage.get('tagName') === 'img') {
            const currentImgSrc = socialImage.get('src') as string;

            if (currentImgSrc) {
              const updatedImgSrc = getUpdatedSocialImageSrcUrl(currentImgSrc, iconStyle);
              const socialImageAttributes = socialImage.getAttributes();

              socialImage.setAttributes({
                ...socialImageAttributes,
                'data-logo-url': updatedImgSrc,
                src: updatedImgSrc,
              });
            }
          }
        });
      });
    }
  });

  cmds.add(CMDS.SYNC_GETTY_COUNT_FOR_COMPONENTS, (editorInstance: Editor): void => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component);
    const mediaTypeImage = window.PMW.emails.mediaType.IMAGE;
    const mediaTypeVideo = window.PMW.emails.mediaType.VIDEO;

    let newGettyImageCount = 0;
    let newGettyVideoCount = 0;
    for (const component of components) {
      const hasGraphic =
        component.get('type') === window.PMW.emails.components.graphic.PMW_IMAGE ||
        component.get('type') === window.PMW.emails.components.graphic.PMW_VIDEO ||
        hasBackgroundImage(component);

      if (component.get(window.PMW.emails.props.CONTENT_SOURCE) === MULTI_MEDIA_SOURCE_GETTY && hasGraphic) {
        const componentMediaType = editorInstance.runCommand(window.PMW.emails.commands.GET_COMPONENT_MEDIA_TYPE, {component}) as string;

        if (componentMediaType === mediaTypeVideo) {
          newGettyVideoCount += 1;
        }

        if (componentMediaType === mediaTypeImage) {
          newGettyImageCount += 1;
        }
      }
    }

    editorInstance.runCommand(CMDS.UPDATE_GETTY_COUNTS_FOR_EMAIL, {
      gettyImageCount: newGettyImageCount,
      gettyVideoCount: newGettyVideoCount,
    });
  });

  cmds.add(
    CMDS.UPDATE_GETTY_COUNTS_FOR_EMAIL,
    (
      editorInstance: Editor,
      _sender: Component,
      opts: {
        gettyImageCount: number;
        gettyVideoCount: number;
      }
    ): void => {
      const wrapper = editorInstance.DomComponents.getWrapper();

      if (wrapper && editorInstance.getComponents().models.length) {
        const mainContent = wrapper.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT}"]`)[0];
        mainContent.set(window.PMW.emails.props.PREMIUM_IMAGE_COUNT, opts.gettyImageCount);
        mainContent.set(window.PMW.emails.props.PREMIUM_VIDEO_COUNT, opts.gettyVideoCount);
      }
    }
  );

  /**
   * @param {object} opts
   * @param {string} opts.hashedFilename
   * @param {string} opts.source one of MULTI_MEDIA_SOURCE_* constants
   * @param {grapesjs.Component} opts.component GrapesJS component to set add selected image to
   * @param {boolean} [opts.isBackground=false]
   * @param {function} [opts.callback] callback to execute on success. Passed image url as parameter
   */
  cmds.add(CMDS.GET_ALL_IMAGES_INFO, async (editorInstance: Editor) => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component);
    const componentsWithInfo: ComponentWithURLMeta[] = [];

    for (const component of components) {
      if (!component) {
        continue;
      }

      if (
        component.get('type') === window.PMW.emails.components.graphic.PMW_IMAGE ||
        component.get('type') === window.PMW.emails.components.graphic.PMW_VIDEO ||
        hasBackgroundImage(component)
      ) {
        const hashedFilename = component.get(window.PMW.emails.props.HASHED_FILENAME) as string;
        const type = component.get(window.PMW.emails.props.MEDIA_TYPE) as string;

        try {
          const {url, source} = (await window.PMW.readLocal('emailmaker/getAssetImageInformation', {
            hashedFilename,
            type,
          })) as {url: string; source: string};

          componentsWithInfo.push({
            component,
            url,
            source,
          });
        } catch (err: any) {
          window.PMW.log(err.detail as string);
          console.error(err.detail as string);
        }
      }
    }

    return componentsWithInfo;
  });

  cmds.add(CMDS.SET_LEGACY_MAIL_COMPATIBILITY, async (editorInstance: Editor, _sender: object) => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component);
    for (const component of components) {
      editor.runCommand(CMDS.FIX_COMPONENT_DEFAULT_PROPS, {component});
      editor.runCommand(CMDS.UPDATE_MS_OUTLOOK_STYLES, {component});
      editor.runCommand(CMDS.DELETE_REDUNDANT_COMPONENTS, {component});
      editor.runCommand(CMDS.UPDATE_LEGACY_DIVIDERS, {component});
    }
  });

  cmds.add(CMDS.UPDATE_BG_IMAGE_GMAIL_COMPATIBILITY, async (editorInstance: Editor, _sender: object) => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component);
    for (const component of components) {
      editor.runCommand(CMDS.REPLACE_BG_IMAGE_ATTRIBUTE_WITH_STYLE, {component});
    }
  });

  cmds.add(CMDS.REPLACE_BG_IMAGE_ATTRIBUTE_WITH_STYLE, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const {component} = opts;
    if (!component) {
      return;
    }

    const style = component.get('style') as StyleProps;
    const attributes = component.get('attributes') as StyleAttributes;

    if (style?.['background-image']) {
      const backgroundImage = style['background-image'] as string;
      const url = /url\((.*?)\)/i.exec(backgroundImage);
      if (url?.[1] && isTableComponent(component)) {
        component.addAttributes({
          background: url[1],
        });
      }
    } else if (attributes?.background) {
      const url = attributes.background;
      if (url && isTableComponent(component)) {
        component.addStyle({
          'background-image': `url(${url})`,
        });
      }
    }
  });

  cmds.add(CMDS.UPDATE_BUTTON_AOL_COMPATIBILITY, (editorInstance: Editor, _sender: object) => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component);

    for (const component of components) {
      if (component.get('type') === window.PMW.emails.components.text.PMW_BUTTON) {
        const buttonChild = component.getChildAt(0);
        if (buttonChild && buttonChild.get('tagName') === 'table') {
          continue;
        }

        component.components([editor.runCommand(CMDS.GET_TABLE_COMPONENT_FOR_BUTTON, {text: component.getInnerHTML()})]);
      }
    }
  });

  cmds.add(
    CMDS.GET_TABLE_COMPONENT_FOR_BUTTON,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        text: string;
      }
    ) => {
      const text = opts.text || window.i18next.t('pmwjs_button_click');

      return {
        type: window.PMW.emails.components.table.PMW_TABLE,
        tagName: 'table',
        highlightable: false,
        hoverable: false,
        selectable: false,
        badgable: false,
        droppable: false,
        draggable: false,
        copyable: false,
        editable: false,
        style: {
          'text-align': 'inherit',
          width: '100%',
          'font-weight': 'inherit',
          'font-style': 'inherit',
          color: 'inherit',
          'font-size': 'inherit',
          'line-height': 'inherit',
        },
        components: [
          {
            type: window.PMW.emails.components.table.PMW_ROW,
            tagName: 'tr',
            highlightable: false,
            hoverable: false,
            selectable: false,
            badgable: false,
            droppable: false,
            draggable: false,
            copyable: false,
            editable: false,
            components: [
              {
                type: window.PMW.emails.components.table.PMW_CELL,
                tagName: 'td',
                highlightable: false,
                hoverable: false,
                selectable: false,
                badgable: false,
                droppable: false,
                draggable: false,
                copyable: false,
                editable: false,
                style: {
                  'text-align': 'inherit',
                  width: 'auto',
                },
                components: [
                  {
                    type: window.PMW.emails.components.text.PMW_TEXT,
                    highlightable: false,
                    hoverable: false,
                    selectable: false,
                    badgable: false,
                    droppable: false,
                    draggable: false,
                    copyable: false,
                    editable: true,
                    style: {
                      margin: '0',
                      width: 'auto',
                    },
                    components: text,
                  },
                ],
              },
            ],
          },
        ],
      };
    }
  );

  const isTableComponent = (component: Component): boolean => {
    const tagName = component.get('tagName')?.toLowerCase();
    return tagName === 'table' || tagName === 'tr' || tagName === 'td';
  };

  const getAllComponents = (model: Component, types: string[] = [], result: Component[] = []): Component[] => {
    const type = model.get('type') ?? '';
    if (types.length === 0 || (type && types.includes(type))) {
      result.push(model);
    }

    model.components().each((mod: Component) => {
      return getAllComponents(mod, types, result);
    });

    return result;
  };

  cmds.add(CMDS.UPDATE_TABLE_OUTLOOK_COMPATIBILITY, (editorInstance: Editor) => {
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component);

    for (const component of components) {
      if (editorInstance.runCommand(CMDS.DOES_TABLE_REQUIRE_TBODY, {component})) {
        const child = component.getChildAt(0);
        component.components({type: window.PMW.emails.components.table.PMW_TBODY, components: [child]});
      }
    }
  });

  cmds.add(CMDS.DOES_TABLE_REQUIRE_TBODY, (_: Editor, __: object, opts: BasicComponentCommandProps): boolean => {
    const {component} = opts;
    const componentParent = component?.parent();
    const componentChild = component?.getChildAt(0);

    if (!component) {
      return false;
    }

    if (!component.is(window.PMW.emails.components.table.PMW_TABLE) && !component.is(window.PMW.emails.components.table.PMW_SECTION)) {
      return false;
    }

    if (component.is(window.PMW.emails.components.table.PMW_TABLE) && componentParent?.is(window.PMW.emails.components.text.PMW_BUTTON)) {
      return false;
    }

    return componentChild?.get('tagName')?.toLowerCase() !== 'tbody';
  });

  cmds.add(CMDS.UPDATE_LEGACY_DIVIDERS, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const {component} = opts;

    if (component && component.get('type') === window.PMW.emails.components.misc.PMW_DIVIDER_LEGACY) {
      const styles = component.getStyle();
      const replacedComponents = component.replaceWith(
        {
          type: window.PMW.emails.components.misc.PMW_DIVIDER,
        },
        {silent: true}
      );

      if (replacedComponents.length === 0) {
        return;
      }

      const newDivider = replacedComponents[0];

      let newStyles = newDivider.getStyle();
      let style;

      if (!newStyles) {
        // eslint-disable-next-line
        // @ts-ignore
        newStyles = [];
      }

      for (style in styles) {
        newStyles[style] = styles[style];
      }

      newDivider.setStyle(newStyles, {silent: true});
    }
  });

  cmds.add(CMDS.COPY_TEXT_FOR_MARKETING_EMAIL, async (): Promise<void> => {
    const res = (await window.PMW.readLocal(`emailmaker/getEmailTemplateTextContent/${window.PMW.idEmailTemplate}`)) as {
      text: string;
    };
    await window.PMW.copyEmailasText(res.text);
  });

  cmds.add(CMDS.COPY_HTML_FOR_MARKETING_EMAIL, async (editorInstance: Editor): Promise<void> => {
    await window.PMW.copyEmailHTMLasText(
      '',
      async () => {
        return (await editorInstance.runCommand(CMDS.GET_INLINED_HTML_FOR_MARKETING)) as EmailPreviewResponse;
      },
      ''
    );
  });

  cmds.add(CMDS.COPY_TEXT_JSON, async (editorInstance: Editor): Promise<void> => {
    const componentsData: TextComponentData[] = getEmailTextWithCcid(editorInstance);
    await window.PMW.copyEmailTextAsJson(JSON.stringify(componentsData));
  });

  cmds.add(CMDS.COPY_MEDIA_QUERIES_FOR_MARKETING_EMAIL, async (editorInstance: Editor): Promise<void> => {
    const mediaQueries = editorInstance.runCommand(CMDS.GET_MEDIA_QUERIES, {}) as string;
    await window.PMW.copyEmailHTMLasText(mediaQueries, $.noop, '');
  });

  cmds.add(CMDS.OPEN_COMPONENT_INFO_MODAL, async (): Promise<void> => {
    window.PMW.showLoading(CMDS.OPEN_COMPONENT_INFO_MODAL, {text: window.i18next.t('pmwjs_loading')});
    const componentsData = (await editor.runCommand(CMDS.GET_ALL_IMAGES_INFO)) as ComponentWithURLMeta[];
    let gridItem = '';

    const getGridItemHTML = (url: string, source: string, id: string): string => {
      return `<div class="js-component-info-grid-item flex-v-row spacing-m-2 border-s-darker ct components-info-grid-item" data-id="${id}"><img src="${url}" /><p class="spacing-p-2 body-lg-bold spacing-m-0">Source: ${source}</p></div>`;
    };

    const ID_DIALOG = 'open-modal-components-info';
    const dialog = window.PMW.openModalDialog(ID_DIALOG, 'Components information', null, true, 'full-screen');
    dialog.find('.content').html('<div class="flex-h-row js-components-info-grid components-info-grid"></div>');

    const parentGrid = dialog.find('.js-components-info-grid');

    for (const data of componentsData) {
      const {component, url, source} = data;
      gridItem = getGridItemHTML(url, source, component.get('attributes')?.id as string);
      parentGrid.append(gridItem);
    }

    dialog.on('click', '.js-component-info-grid-item', function () {
      const t = $(this);
      const matched = editor.getWrapper()?.find(`#${t.attr('data-id')}`);

      if (matched && matched.length > 0) {
        const component = matched[0];
        editor.select(component);
        editor.Canvas.scrollTo(component, {
          force: true,
          behavior: 'smooth',
        });

        window.PMW.closeModalDialog(ID_DIALOG);
      } else {
        alert('Component not found');
      }
    });

    window.PMW.hideLoading(CMDS.OPEN_COMPONENT_INFO_MODAL);
  });

  cmds.add(CMDS.GET_COMPONENT_MEDIA_TYPE, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const component = opts.component || editor.getSelected();

    return component?.get('type') === window.PMW.emails.components.graphic.PMW_VIDEO ? window.PMW.emails.mediaType.VIDEO : window.PMW.emails.mediaType.IMAGE;
  });

  cmds.add(
    CMDS.SET_EMBEDDED_VIDEO_SRC,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        thumbnailURL: string;
        videoURL: string;
      }
    ) => {
      const component = opts.component || editor.getSelected();
      if (!component) {
        return;
      }

      const styles = component.getStyle();
      styles['max-width'] = '100%';
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: opts.component, loading: false});
      component.set('src', opts.thumbnailURL);
      component.addAttributes({'data-is-pmw-embedded-video': 1});
      component.setStyle(styles);

      editor.runCommand(CMDS.ADD_COMPONENT_ANCHOR, {
        component: opts.component,
        url: opts.videoURL,
        shouldOpenInNewTab: true,
      });

      const el = $('.pmw-email-url-input');

      if (!el) {
        return;
      }

      el.val(opts.videoURL);
    }
  );

  /**
   * @param {object} opts
   * @param {string} opts.hashedFilename
   * @param {string} opts.source one of MULTI_MEDIA_SOURCE_* constants
   * @param {grapesjs.Component} opts.component GrapesJS component to set add selected image to
   * @param {boolean} [opts.isBackground=false]
   * @param {function} [opts.callback] callback to execute on success. Passed image url as parameter
   */
  cmds.add(CMDS.SET_IMAGE_SRC, async (editorInstance: Editor, _sender: object, opts: BackgroundRemoveURLProps & {showLoadingToast?: boolean}) => {
    let cropData;
    const {hashedFilename, source, component, backgroundRemovedURL, showLoadingToast = true} = opts;

    const prevSource = component.get(window.PMW.emails.props.CONTENT_SOURCE) as string;
    const isNewImage = component.get(window.PMW.emails.props.HASHED_FILENAME) !== opts.hashedFilename;

    if (isNewImage) {
      component.set(window.PMW.emails.props.CROP_DATA, undefined);
      component.set(window.PMW.emails.props.UNCROPPED_SRC, undefined);
      component.set(window.PMW.emails.props.REMOVED_BACKGROUND_URL, undefined);
    } else {
      cropData = component.get(window.PMW.emails.props.CROP_DATA) as {
        x: number;
        y: number;
        width: number;
        height: number;
        imageWidth: number;
        imageHeight: number;
      };
      cropData = cropData && new CropData(cropData);
    }

    editor.runCommand(CMDS.SET_IMAGE_LOADING, {
      component,
      loading: true,
      text: cropData ? window.i18next.t('pmwjs_cropping') : window.i18next.t('pmwjs_loading'),
    });

    const removedBackgroundURL = component.get(window.PMW.emails.props.REMOVED_BACKGROUND_URL) as string;
    const info = await getImageInfo(component, source, hashedFilename, showLoadingToast, cropData, removedBackgroundURL);

    if (opts.isBackground) {
      editor.runCommand(CMDS.SET_COMPONENT_STYLE, {
        component,
        value: `url(${info.url})`,
        property: 'background-image',
      });
    } else {
      if (backgroundRemovedURL) {
        component.set(window.PMW.emails.props.UNCROPPED_SRC, backgroundRemovedURL);
      }

      component.set('src', info.url);
      editor.runCommand(CMDS.UPDATE_MS_OUTLOOK_STYLES, {component});
    }

    component.set(window.PMW.emails.props.CONTENT_SOURCE, source);
    component.set(window.PMW.emails.props.HASHED_FILENAME, opts.hashedFilename);
    editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: opts.component, loading: false});

    syncImageSourceChange(editorInstance, prevSource, opts.source, component);
    if (typeof opts.callback === 'function') {
      opts.callback(info.url);
    }
  });

  /**
   * @param {object} opts
   * @param {CropData} opts.cropData
   * @param {grapesjs.Component} opts.component
   */
  cmds.add(
    CMDS.SET_IMAGE_CROPDATA,
    async (
      _editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        cropData: CropData;
      }
    ) => {
      const {component} = opts;
      let {cropData} = opts;

      if (!cropData || !component?.get(window.PMW.emails.props.IS_CROPPABLE)) {
        return;
      }

      const isFirstCrop = !component.get(window.PMW.emails.props.CROP_DATA);
      if (isFirstCrop) {
        component.set(window.PMW.emails.props.UNCROPPED_SRC, component.get('src'));
      }

      // eslint-disable-next-line
      // @ts-ignore
      cropData = new CropData(cropData);
      component.set(window.PMW.emails.props.CROP_DATA, cropData.isMaxCropped() ? undefined : cropData.toObject());
      editor.runCommand(CMDS.SET_IMAGE_SRC, {
        component,
        source: component.get(window.PMW.emails.props.CONTENT_SOURCE),
        hashedFilename: component.get(window.PMW.emails.props.HASHED_FILENAME),
      });

      // Tracking temporarily disabled on 8th Nov 2023
      // editor.runCommand(CMDS.TRACK_GA_EVENT_COMPONENT, {
      //     component: component,
      //     eventAction: 'EmailMaker_Crop'
      // });
    }
  );

  /**
   * @param {grapesjs.Component} component
   * @param {string} source
   * @param {string} hashedFilename
   * @param {boolean} showLoadingToast
   * @param {CropData} [cropData]
   * @param {string} removedBackgroundURL
   * @return {Promise<ImageMeta>}
   * @private
   */
  async function getImageInfo(
    component: Component,
    source: string,
    hashedFilename: string,
    showLoadingToast: boolean,
    cropData?: CropData,
    removedBackgroundURL?: string
  ): Promise<ImageMeta> {
    return cropData
      ? await getCroppedSrcInfo(hashedFilename, cropData, component.get(window.PMW.emails.props.MEDIA_TYPE) as string, removedBackgroundURL!)
      : await getMediaSrcInfo(hashedFilename, source, component.get(window.PMW.emails.props.MEDIA_TYPE) as string, removedBackgroundURL!, showLoadingToast);
  }

  /**
   * @param {string} hashedFilename
   * @param {string} source
   * @param {string} type
   * @param {string} removedBackgroundURL
   * @param {boolean} showLoadingToast
   * @return {Promise<ImageMeta>}
   * @private
   */
  async function getMediaSrcInfo(hashedFilename: string, source: string, type: string, removedBackgroundURL: string, showLoadingToast: boolean): Promise<ImageMeta> {
    const info: ImageMeta = {
      url: '',
      width: 0,
      height: 0,
    };

    try {
      const res: ImageMeta = await window.PMW.readLocal('emailmaker/getEmailMediaURL', {
        hashedFilename,
        type,
        removedBackgroundURL,
      });
      info.url = res.url;
      info.width = res.width;
      info.height = res.height;
    } catch (e: any) {
      console.warn('Image email url not found. Creating new preset.');

      if (showLoadingToast) {
        window.PMW.showLoading('emailPresetCreation', {text: window.i18next.t('pmwjs_optimising_upload')});
      }

      if (e.code === 404) {
        try {
          const res: ImageMeta = (await window.PMW.pollingAjaxCallAsync(
            async (): Promise<void> => {
              return createEmailMediaPresets(hashedFilename, source, type);
            },
            async (): Promise<void> => {
              return checkEmailMediaPresetsStatus(hashedFilename, type);
            }
          )) as ImageMeta;
          info.url = res.url;
          info.width = res.width;
          info.height = res.height;
        } catch (e2: any) {
          window.PMW.openErrorModal({message: e2.message as string});
        }
      }
    } finally {
      if (showLoadingToast) {
        window.PMW.hideLoading('emailPresetCreation');
      }
    }

    return info;
  }

  /**
   * @param {string} hashedFilename
   * @param {CropData} cropData
   * @param {string} type
   * @param {string} removedBackgroundURL
   * @return {Promise<object>} {url: string, width: number, height: number}
   * @private
   */
  async function getCroppedSrcInfo(hashedFilename: string, cropData: CropData, type: string, removedBackgroundURL: string): Promise<ImageMeta> {
    try {
      return (await window.PMW.writeLocal('emailmaker/getCroppedMediaURL', {
        type,
        hashedFilename,
        ...cropData.toObject(),
        removedBackgroundURL,
      })) as ImageMeta;
    } catch (e) {
      console.error(e);
      window.PMW.openErrorModal();
    }

    return {url: '', width: 0, height: 0};
  }

  cmds.add(CMDS.UPDATE_STYLE_BUTTON_STATE, () => {
    if (window.PMW.emails.COPIED_STYLES_COMPONENT) {
      $('.js-paste-styles').removeClass('-disabled');
    }
  });

  /**
   * @param {object} opts
   * @param {grapesjs.Component} opts.component
   * @param {boolean} opts.loading
   * @param {string} [opts.text]
   */
  cmds.add(CMDS.COPY_STYLES, (editorInstance: Editor, _, opts: BasicComponentCommandProps) => {
    const component = opts.component ?? editorInstance.getSelected();
    if (!component) {
      return;
    }

    window.PMW.emails.COPIED_STYLES_COMPONENT = component;
    document.dispatchEvent(new CustomEvent(window.PMW.emails.CUSTOM_EVENT_COMPONENT_STYLE_COPIED, {}));
  });

  /**
   * @param {object} opts
   * @param {grapesjs.Component} opts.component
   * @param {boolean} opts.loading
   * @param {string} [opts.text]
   */
  cmds.add(CMDS.PASTE_STYLES, (editorInstance: Editor, _, opts: BasicComponentCommandProps) => {
    if (window.PMW.emails.COPIED_STYLES_COMPONENT) {
      const component = opts.component ?? editorInstance.getSelected();

      if (component) {
        // eslint-disable-next-line
        // @ts-ignore
        const styles = window.PMW.emails.COPIED_STYLES_COMPONENT.getStyle();

        for (const key in styles) {
          if (window.PMW.emails.NONCOPYABLE_STYLES.includes(key)) {
            continue;
          }

          editorInstance.runCommand(CMDS.SET_COMPONENT_STYLE, {
            component,
            property: key,
            value: styles[key],
          });
        }
      }
    }
  });

  /**
   * @param {object} opts
   * @param {grapesjs.Component} opts.component
   * @param {boolean} opts.loading
   * @param {string} [opts.text]
   */
  cmds.add(
    CMDS.SET_IMAGE_LOADING,
    (
      editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        text: string;
        loading: boolean;
      }
    ) => {
      editorInstance.getModel().skip(() => {
        if (!opts.component) {
          return;
        }

        if (opts.loading) {
          opts.component.addClass('-loading');
          window.PMW.showLoading(`${opts.component.cid}:img-loading`, {text: opts.text});
        } else {
          opts.component.removeClass('-loading');
          window.PMW.hideLoading(`${opts.component.cid}:img-loading`);
        }
      });
    }
  );

  cmds.add(CMDS.REMOVE_BUGGY_BACKGROUND_ATTRIBUTE, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const {component} = opts;

    if (component) {
      const attributes = component.getAttributes();
      delete attributes.background;
      component.setAttributes(attributes);
    }
  });

  function syncImageSourceChange(editorInstance: Editor, prevSrc: string, newSrc: string, component: Component): void {
    const gettySrc = MULTI_MEDIA_SOURCE_GETTY;
    const switchedToGetty = newSrc === gettySrc && prevSrc !== gettySrc;
    const switchedFromGetty = newSrc !== gettySrc && prevSrc === gettySrc;

    if (switchedToGetty || switchedFromGetty) {
      editorInstance.runCommand(CMDS.UPDATE_GETTY_COUNT, {
        deltaCount: switchedToGetty ? 1 : -1,
        type: editorInstance.runCommand(window.PMW.emails.commands.GET_COMPONENT_MEDIA_TYPE, {component}),
      });
    }
  }

  function updateRowsInTable(tableComponent: Component, totalRows: number): void {
    let currentRows = tableComponent.components().length;
    const requiredRows = totalRows - currentRows;

    // Tracking temporarily disabled on 8th Nov 2023
    // PMW.gtm.trackGA4CustomEvent('EmailMaker_UpdateRow');

    if (requiredRows < 0) {
      const rowComponentsToRemove: Component[] = [];
      tableComponent.components().each((child, i) => {
        if (i < totalRows) {
          return;
        }

        if (child) {
          rowComponentsToRemove.push(child);
        }
      });

      rowComponentsToRemove.forEach((row) => {
        row.remove();
      });
    } else {
      let currentCells = 0;

      tableComponent.components().each((child) => {
        currentCells = child.components().length;
      });

      const cellsToAdd = [];

      for (let i = 0; i < currentCells; i++) {
        cellsToAdd.push({
          type: window.PMW.emails.components.table.PMW_CELL,
        });
      }

      for (currentRows; currentRows < totalRows; currentRows++) {
        const row = tableComponent.components().add(
          {
            type: window.PMW.emails.components.table.PMW_ROW,
            components: cellsToAdd,
          },
          {at: currentRows}
        ) as unknown as Component;

        for (let i = 0; i < row.components().length; i++) {
          row.components().each((cell) => {
            editor.runCommand(CMDS.APPLY_GLOBAL_CELL_STYLES, {cell});
          });
        }
      }
    }
  }

  const updateColumnsInRow = (rowComponent: Component, totalCells: number, insertEmpty = false) => {
    let currentCells = rowComponent.components().length;
    const requiredCells = totalCells - currentCells;
    // Tracking temporarily disabled on 8th Nov 2023
    // PMW.gtm.trackGA4CustomEvent('EmailMaker_UpdateColumn');

    if (requiredCells < 0) {
      const cellComponentsToRemove: Component[] = [];
      const cellsToRemove = Math.abs(requiredCells);
      const startIndex = rowComponent.components().length - cellsToRemove;

      rowComponent.components().each((child, i) => {
        if (child && i >= startIndex) {
          cellComponentsToRemove.push(child);
        }
      });

      cellComponentsToRemove.forEach((cell) => {
        cell.remove();
      });
    } else {
      let cell: Component;
      for (currentCells; currentCells < totalCells; currentCells++) {
        cell = rowComponent.components().add({
          type: window.PMW.emails.components.table.PMW_CELL,
        }) as unknown as Component;

        if (insertEmpty) {
          cell.empty();
        }

        editor.runCommand(CMDS.APPLY_GLOBAL_CELL_STYLES, {cell, parent: rowComponent});
      }
    }
  };

  /**
   * @param {grapesjs.Component} rowComponent grapesJS row component
   * @param {number[]} columnWidths
   * @param {string} widthUnit
   */
  const updateColumnWidthsFromPreset = (rowComponent: Component, columnWidths: number[], widthUnit: string) => {
    const rowCells = rowComponent.components();
    rowCells.map((cell, i) => {
      const style = cell.getStyle();
      style.width = columnWidths[i] + widthUnit;
      cell.setStyle(style);
    });
  };

  /**
   * @param {object} opts
   * @param {boolean} opts.responsive whether to keep cells responsive or not
   * @param {ResponsiveType} opts.type whether to keep cells responsive or not
   * @param {grapesjs.Component} opts.component
   */
  cmds.add(
    CMDS.UPDATE_SECTION_RESPONSIVE,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        responsive: boolean;
        type: ResponsiveType;
      }
    ) => {
      const component = opts.component || editor.getSelected();
      if (!component) {
        return;
      }
      const unresponsiveClass = 'pmw-table';
      let responsiveClass = 'pmw-responsive-table';
      let responsiveAttributeName = ResponsiveAttributeName.MOBILE;

      if (opts.type === ResponsiveType.TABLET) {
        responsiveClass = 'pmw-responsive-table-tablet';
        responsiveAttributeName = ResponsiveAttributeName.TABLET;
      }

      component.set(responsiveAttributeName, opts.responsive);
      if (opts.responsive) {
        component.addClass(responsiveClass);
        component.removeClass(unresponsiveClass);
      } else {
        component.addClass(unresponsiveClass);
        component.removeClass(responsiveClass);
      }
    }
  );

  cmds.add(CMDS.UPDATE_TABLE_ROWS, (editorInstance: Editor, _sender: object, opts: {sliderVal: number}) => {
    if (!editorInstance.getSelected()) {
      return;
    }

    const tableBody: Component | null = editor.runCommand(CMDS.GET_PRIMARY_TABLE_COMPONENT, {component: editorInstance.getSelected()});

    if (!tableBody) {
      return;
    }

    updateRowsInTable(tableBody, opts.sliderVal);
  });

  cmds.add(
    CMDS.APPLY_GLOBAL_CELL_STYLES,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: CellPropertyUpdateProps & {
        parent: Component;
      }
    ) => {
      const parent = opts.parent || editor.getSelected();
      const styles = parent.getStyle();
      const borderWidth: string | undefined = styles['pmw-cell-border-width'] as string;

      if (borderWidth) {
        editor.runCommand(CMDS.UPDATE_CELL_BORDER_WIDTH, {
          sliderVal: parseInt(borderWidth, 10),
          cell: opts.cell,
          global: true,
        });
      }

      const borderRadius: string | undefined = styles['pmw-cell-border-radius'] as string;

      if (borderRadius) {
        editor.runCommand(CMDS.UPDATE_CELL_BORDER_RADIUS, {
          sliderVal: parseInt(borderRadius, 10),
          cell: opts.cell,
          global: true,
        });
      }

      if (typeof styles['pmw-cell-background-color'] !== 'undefined') {
        editor.runCommand(CMDS.UPDATE_CELL_BACKGROUND_COLOR, {
          color: styles['pmw-cell-background-color'],
          cell: opts.cell,
          global: true,
        });
      }

      if (typeof styles['pmw-cell-border-color'] !== 'undefined') {
        editor.runCommand(CMDS.UPDATE_CELL_BORDER_COLOR, {
          color: styles['pmw-cell-border-color'],
          cell: opts.cell,
          global: true,
        });
      }
    }
  );

  cmds.add(CMDS.GET_SECTION_COLUMNS_PRESETS, (): PresetComponentVO[] => {
    return [
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 1, [100], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 2, [50, 50], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 3, [30, 70], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 4, [70, 30], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 5, [20, 80], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 6, [80, 20], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 7, [25, 50, 25], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 8, [33, 33, 33], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 9, [25, 25, 50], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 10, [50, 25, 25], '%'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 11, [25, 25, 25, 25], '%'),
    ];
  });

  /**
   * adds or removes columns and sets responsiveness
   * @param {object} opts
   * @param {number} opts.presetID
   * @param {number[]} opts.columnWidths
   * @param {string} opts.widthUnit
   */
  cmds.add(
    CMDS.UPDATE_COLUMNS_PRESET,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        presetID: number;
      } & BasicComponentCommandProps
    ) => {
      const presets = editor.runCommand(CMDS.GET_SECTION_COLUMNS_PRESETS) as PresetComponentVO[];
      const presetToApply = presets.find((preset) => preset.id === opts.presetID);
      if (!presetToApply) {
        return;
      }
      const columnWidths = presetToApply.compValues as number[];
      const widthUnit = presetToApply.classUnit;

      const selected = opts.component ?? editor.getSelected();
      if (!selected) {
        return;
      }

      const tableBody: Component | null = editor.runCommand(CMDS.GET_PRIMARY_TABLE_COMPONENT, {component: selected});

      if (!tableBody) {
        return;
      }

      tableBody.get('components')?.each((row) => {
        updateColumnsInRow(row, columnWidths.length, true);
        updateColumnWidthsFromPreset(row, columnWidths, widthUnit);
      });

      editor.runCommand(CMDS.UPDATE_SECTION_RESPONSIVE, {
        component: tableBody,
        responsive: tableBody.get(window.PMW.emails.props.IS_RESPONSIVE_MOBILE),
        type: ResponsiveType.MOBILE,
      });

      editor.runCommand(CMDS.UPDATE_SECTION_RESPONSIVE, {
        component: tableBody,
        responsive: tableBody.get(window.PMW.emails.props.IS_RESPONSIVE_TABLET),
        type: ResponsiveType.TABLET,
      });
    }
  );

  cmds.add(CMDS.GET_PRIMARY_TABLE_COMPONENT, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): Component | null => {
    const selected = opts.component || editor.getSelected();
    if (!selected) {
      return null;
    }

    const tableComponents = selected.components();

    const potentialTableBody = tableComponents.find((child) => {
      return child.get('tagName')?.toLowerCase() === window.PMW.emails.components.default.TBODY;
    });

    if (potentialTableBody) {
      return potentialTableBody;
    }

    return selected;
  });

  cmds.add(CMDS.UPDATE_TABLE_COLUMNS, (_editorInstance: Editor, _sender: object, opts: {sliderVal: number}) => {
    const selected = editor.getSelected();
    if (!selected) {
      return;
    }

    const tableBody: Component | null = editor.runCommand(CMDS.GET_PRIMARY_TABLE_COMPONENT, {component: selected});

    if (!tableBody) {
      return;
    }

    tableBody.get('components')?.each((child) => {
      updateColumnsInRow(child, opts.sliderVal);
    });
  });

  cmds.add(
    CMDS.UPDATE_CELLS_SPACING,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        sliderVal: number;
        cellspacing: number;
      }
    ) => {
      const selected = editor.getSelected();
      if (!selected) {
        return;
      }

      const styles = selected.getStyle();

      styles['mso-cellspacing'] = opts.sliderVal as unknown as string;
      selected.setStyle(styles);
      selected.addAttributes({
        cellspacing: opts.sliderVal,
      });
    }
  );

  /**
   * @param {object} opts
   * @return void
   */
  cmds.add(CMDS.UPDATE_TEXT_COMPONENT_LINK_COLOR, (editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    let component = opts.component || editorInstance.getSelected();

    if (!component) {
      return;
    }

    let color: string | undefined;

    if (component.is(window.PMW.emails.components.text.PMW_TEXT)) {
      color = getTextLinkColor(component);
    }

    if (component.is(window.PMW.emails.components.text.PMW_LINK)) {
      const parent = component.parent();
      if (parent?.is(window.PMW.emails.components.text.PMW_TEXT)) {
        color = getTextLinkColor(parent);
      }

      component = parent;
    }

    if (!color) {
      return;
    }

    editorInstance.runCommand(CMDS.UPDATE_LINKS_COLOR, {component, color});
  });

  /**
   * @param {object} opts
   * @return void
   */
  cmds.add(CMDS.UPDATE_MERGE_TAGS, (editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const component = opts.component ?? editorInstance.getSelected();

    if (!component) {
      return;
    }

    if (!component.is(window.PMW.emails.components.text.PMW_TEXT)) {
      return;
    }

    hydrateMergeTagsReduxFromComponent(component);
  });

  cmds.add(
    CMDS.UPDATE_LINKS_COLOR,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        color: string;
      }
    ) => {
      const {component} = opts;

      if (component) {
        component.find(`[data-gjs-type="${window.PMW.emails.components.text.PMW_LINK}"]`).forEach((linkComponent) => {
          const style = linkComponent.getStyle();
          style.color = opts.color;
          linkComponent.setStyle(style);
        });
      }
    }
  );

  cmds.add(CMDS.UPDATE_CELL_BORDER_WIDTH, (editorInstance: Editor, _sender: object, opts: CellPropertyUpdateProps) => {
    if (typeof opts.cell !== 'undefined') {
      const component = editorInstance.getSelected();

      if (!component) {
        return;
      }

      if (opts.global && !isGlobalStyleChangeApplicable(component, opts.cell, 'pmw-cell-border-width', 'border-width')) {
        return;
      }

      const style: StyleAttributes = {};
      style['border-width'] = `${opts.sliderVal}px`;
      style['border-style'] = 'solid';
      opts.cell.addStyle(style);
    }
  });

  cmds.add(CMDS.UPDATE_CELLS_BORDER_WIDTH, (_editorInstance: Editor, _sender: object, opts: CellPropertyUpdateProps) => {
    const selected = editor.getSelected();
    if (!selected) {
      return;
    }

    const tableBody: Component | null = editor.runCommand(CMDS.GET_PRIMARY_TABLE_COMPONENT, {component: selected});

    if (!tableBody) {
      return;
    }

    tableBody.get('components')?.each((row) => {
      row.components().each((cell) => {
        editor.runCommand(CMDS.UPDATE_CELL_BORDER_WIDTH, {
          sliderVal: opts.sliderVal,
          cell,
          global: true,
        });
      });
    });
  });

  cmds.add(CMDS.UPDATE_CELL_BORDER_COLOR, (editorInstance: Editor, _sender: object, opts: CellPropertyUpdateProps) => {
    if (typeof opts.cell !== 'undefined') {
      const component = editorInstance.getSelected();

      if (!component) {
        return;
      }

      if (opts.global && !isGlobalStyleChangeApplicable(component, opts.cell, 'pmw-cell-border-color', 'border-color')) {
        return;
      }

      const style: StyleAttributes = {};
      style['border-color'] = opts.color!;
      opts.cell.addStyle(style);
    }
  });

  cmds.add(CMDS.UPDATE_CELLS_BORDER_COLOR, (_editorInstance: Editor, _sender: object, opts: CellPropertyUpdateProps) => {
    const selected = editor.getSelected();

    const tableComponent: Component | null = editor.runCommand(CMDS.GET_PRIMARY_TABLE_COMPONENT, {component: selected});

    if (!tableComponent) {
      return;
    }

    tableComponent.get('components')?.each((row) => {
      row.components().each((cell) => {
        editor.runCommand(CMDS.UPDATE_CELL_BORDER_COLOR, {color: opts.color, cell, global: true});
      });
    });
  });

  cmds.add(CMDS.UPDATE_CELL_BACKGROUND_COLOR, (editorInstance: Editor, _sender: object, opts: CellPropertyUpdateProps) => {
    if (typeof opts.cell !== 'undefined') {
      const component = editorInstance.getSelected();

      if (!component) {
        return;
      }

      const style: StyleAttributes = {};
      style['background-color'] = opts.color!;
      opts.cell.addStyle(style);
    }
  });

  cmds.add(
    CMDS.UPDATE_CELLS_BACKGROUND_COLOR,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        color: string;
      }
    ) => {
      const selected = editor.getSelected();

      const tableComponent: Component | null = editor.runCommand(CMDS.GET_PRIMARY_TABLE_COMPONENT, {component: selected});

      if (!tableComponent) {
        return;
      }

      tableComponent.get('components')?.each((row) => {
        row.components().each((cell) => {
          editor.runCommand(CMDS.UPDATE_CELL_BACKGROUND_COLOR, {
            color: opts.color,
            cell,
            global: true,
          });
        });
      });
    }
  );

  cmds.add(CMDS.UPDATE_CELL_BORDER_RADIUS, (editorInstance: Editor, _sender: object, opts: CellPropertyUpdateProps) => {
    if (typeof opts.cell !== 'undefined') {
      const component = editorInstance.getSelected();

      if (!component) {
        return;
      }

      if (opts.global && !isGlobalStyleChangeApplicable(component, opts.cell, 'pmw-cell-border-radius', 'border-radius')) {
        return;
      }

      const style: StyleAttributes = {};
      style['border-radius'] = `${opts.sliderVal}px`;
      opts.cell.addStyle(style);
    }
  });

  cmds.add(
    CMDS.UPDATE_CELLS_BORDER_RADIUS,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: {
        sliderVal: number;
      }
    ) => {
      const selected = editor.getSelected();
      if (!selected) {
        return;
      }

      const tableComponent: Component | null = editor.runCommand(CMDS.GET_PRIMARY_TABLE_COMPONENT, {component: selected});

      if (!tableComponent) {
        return;
      }

      tableComponent.get('components')?.each((row) => {
        row.components().each((cell) => {
          editor.runCommand(CMDS.UPDATE_CELL_BORDER_RADIUS, {
            sliderVal: opts.sliderVal,
            cell,
            global: true,
          });
        });
      });
    }
  );

  /**
   * @param {object} opts
   * @param {string} opts.linkToSet
   * @returns {object}
   */
  cmds.add(
    CMDS.GET_UPDATED_LINK_ATTRIBUTES,
    (
      editorInstance: Editor,
      _sender: object,
      opts: {
        linkToSet: string;
      }
    ) => {
      let updatedAttributes: {href?: string; style?: string} = {href: opts.linkToSet};
      const component = editorInstance.getSelected();

      if (!component) {
        return {};
      }

      if (editorInstance.getSelected() !== undefined) {
        const newStyle = `color:${component.getAttributes()[window.PMW.emails.styles.LINK_COLOR]}`;
        updatedAttributes = {href: opts.linkToSet, style: newStyle};
      }

      return updatedAttributes;
    }
  );

  cmds.add(CMDS.FIX_COMPONENT_DEFAULT_PROPS, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): void => {
    if (!opts.component) {
      return;
    }

    const {component} = opts;

    if (component.is(window.PMW.emails.components.table.PMW_ROW) || component.is(window.PMW.emails.components.table.PMW_TBODY)) {
      component.set('draggable', false);
      component.set('removable', false);
      component.set('selectable', false);
      component.set('highlightable', false);
      component.set('badgable', false);
      component.set('hoverable', false);
    }
  });

  /**
   * @param {object} opts
   * @param {grapesjs.Component} opts.component
   * @returns {grapesjs.Component}
   */
  cmds.add(CMDS.GET_COMPONENT_FOR_TOGGLE_SWITCH, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): Component | undefined => {
    const component = opts.component || editor.getSelected();

    if (!component) {
      return undefined;
    }

    if (component.parent() && component.parent()?.get('tagName')?.toLowerCase() === 'a') {
      return component.parent();
    }

    return component;
  });

  const updateStyleFixComponentOverflow = (component: Component): void => {
    editor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: 'display', value: 'block', component});
    editor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: 'width', value: 'unset', component});
  };

  cmds.add(CMDS.FIX_COMPONENT_FULL_WIDTH_OVERFLOW_AOL_YAHOO, (editorInstance: Editor) => {
    const types: string[] = [window.PMW.emails.components.text.PMW_BUTTON, window.PMW.emails.components.social.PMW_SOCIALS];
    const components = getAllComponents(editorInstance.DomComponents.getWrapper() as Component, types);

    for (const component of components) {
      const styles: StyleProps = component.getStyle();
      if (styles.width === '100%' || (styles.display === 'table' && styles.width === 'unset')) {
        updateStyleFixComponentOverflow(component);
      }
    }
  });

  cmds.add(CMDS.LISTENER_PASTE_AS_TEXT, (editorInstance: Editor) => {
    const iframeBody: HTMLElement = editor.Canvas.getBody();
    $(iframeBody).on('paste', '[contenteditable="true"]', (e: JQuery.TriggeredEvent) => {
      e.stopPropagation();
      e.preventDefault();

      const clipboardEvent = e.originalEvent as ClipboardEvent;
      const text: string | undefined = clipboardEvent.clipboardData?.getData('text');

      if (text) {
        const formattedData = text.replace(/(\r\n|\n|\r)/g, '<br>');
        const target = e.target as HTMLElement;
        target.ownerDocument.execCommand('insertHTML', false, formattedData);

        const component = editorInstance.getSelected();
        if (component) {
          editorInstance.trigger('component:update', component);
        }
      }
    });
  });

  cmds.add(CMDS.IS_COMPONENT_WIDTH_UNSET, (_editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): boolean => {
    const component = opts.component || editor.getSelected();
    if (!component) {
      return false;
    }

    const {width} = component.getStyle();
    return width === 'unset';
  });

  cmds.add(
    CMDS.ADD_COPY_TABLE_STYLE_TO_TABLE_BODY_LISTENER,
    (
      _editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        stylesToCopy: string[];
      }
    ): void => {
      const component = opts.component || editor.getSelected();
      const {stylesToCopy} = opts;

      if (!component) {
        return;
      }

      const tableComponents = component.components();
      if (tableComponents.length !== 1) {
        return;
      }

      const listener = stylesToCopy
        .map((style) => {
          return `change:style:${style}`;
        })
        .join(' ');
      component.on(listener, () => {
        const tableBody = tableComponents.at(0);
        const currentStyles = component.getStyle();
        const newStyles: Record<string, any> = [];

        for (const style of stylesToCopy) {
          let value = currentStyles[style];
          if (style === 'background-color' && editor.runCommand(CMDS.DOES_TABLE_HAVE_ROUNDNESS, {component})) {
            value = 'transparent';
          }

          if (value) {
            newStyles[style] = value;
          }
        }

        tableBody.setStyle(newStyles);
      });
    }
  );

  cmds.add(CMDS.GET_PENDING_MARKETING_POPUP, (): void => {
    if (typeof window.PMW.marketing !== 'undefined') {
      window.PMW.marketing.showPendingMarketingPopups();
    }
  });

  cmds.add(CMDS.IS_MULTISELECT, (): boolean => {
    return editor.getSelectedAll().length > 1;
  });

  const getUpdatedSocialImageSrcUrl = (currentImgUrl: string, iconStyleDirectory: string): string => {
    const SOCIAL_ICONS_DIRECTORY_NAME = '/social-icons/';
    const [baseUrl, styleSpecificImagePath] = currentImgUrl.split(SOCIAL_ICONS_DIRECTORY_NAME);
    const [, ...imageFileName] = styleSpecificImagePath.split('/');
    const updatedImagePath = [iconStyleDirectory, ...imageFileName].join('/');

    return `${baseUrl}${SOCIAL_ICONS_DIRECTORY_NAME}${updatedImagePath}`;
  };

  const getTextLinkColor = (textComponent: Component): string | undefined => {
    const styles = textComponent.getStyle();
    if (typeof styles['pmw-link-color'] === 'undefined') {
      return undefined;
    }

    return styles['pmw-link-color'] as string;
  };

  const shouldAllowSavingSectionTemplate = (component?: Component): boolean => {
    if (!component?.is(window.PMW.emails.components.table.PMW_SECTION)) {
      return false;
    }

    const wrapper = editor.DomComponents.getWrapper();

    return !!wrapper;
  };

  cmds.add(CMDS.GET_INLINE_STYLED_COMPONENT, (editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): string => {
    const component = opts.component ?? editorInstance.getSelected();

    if (!component) {
      return '';
    }

    const template = `${component.toHTML()}<style>${editorInstance.getCss()}</style>`;

    return juice(template, {});
  });

  cmds.add(CMDS.SAVE_COMPONENT_TEMPLATE, (editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps) => {
    const section = opts.component ?? editorInstance.getSelected();

    if (!section || !shouldAllowSavingSectionTemplate(section)) {
      return;
    }

    const sectionHtmlContent = editor.runCommand(CMDS.GET_INLINE_STYLED_COMPONENT, {component: section}) as string;
    const sectionJsonContent = JSON.stringify(section.toJSON());
    const htmlMediaQueries = editor.runCommand(CMDS.GET_MEDIA_QUERIES) as string;

    const selectCategoryBox = document.createElement('div');
    window.PMW.renderAdminSaveSectionTemplatePanel(selectCategoryBox, {
      sectionHtmlContent,
      sectionJsonContent,
      htmlMediaQueries,
    });

    const SAVE_COMPONENT_TEMPLATE_MODAL_ID = 'save-component-template-modal';
    const saveComponentModel = window.PMW.openModalDialog(SAVE_COMPONENT_TEMPLATE_MODAL_ID, window.i18next.t('pmwjs_save_as_section_template'), {}, true, '-modal-400');
    saveComponentModel.find('.content').html(selectCategoryBox);
  });

  cmds.add(
    CMDS.SAVE_USER_COMPONENT_TEMPLATE,
    (
      editorInstance: Editor,
      _sender: object,
      opts: BasicComponentCommandProps & {
        callback: VoidFunction;
      }
    ) => {
      if (!isUserLoggedIn()) {
        window.PMW.hideLoading();

        openAuthenticateModal({
          signupSource: 'email-maker-save-section',
          panelText: window.i18next.t('pmwjs_make_account_to_save_email'),
          panelSubtext: window.i18next.t('pmwjs_login_emails_promotion_subtext'),
          onLoginSuccess: async () => {
            window.PMW.showLoading(null, {text: window.i18next.t('pmwjs_saving')});
            await editor.runCommand(CMDS.SAVE_USER_COMPONENT_TEMPLATE, {
              editorInstance,
              component: opts.component,
              callback: opts.callback,
            });
          },
        });

        return;
      }

      const section = opts.component ?? editorInstance.getSelected();

      if (!section || !shouldAllowSavingSectionTemplate(section)) {
        return;
      }

      const sectionHtmlContent = editor.runCommand(CMDS.GET_INLINE_STYLED_COMPONENT, {component: section}) as string;
      const htmlMediaQueries = editor.runCommand(CMDS.GET_MEDIA_QUERIES) as string;

      window.PMW.writeLocal('emailcomponenttemplate/saveUserTemplate', {
        htmlContent: sectionHtmlContent,
        jsonContent: JSON.stringify(section),
        mediaQueries: htmlMediaQueries,
        name: window.i18next.t('pmwjs_section'),
      })
        .then((response: UserEmailComponentTemplate) => {
          store.dispatch(addUserTemplate(response));
          window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_SECTION_SAVE);
          void window.PMW.writeLocal('emailcomponenttemplate/createUserTemplatePreview', {
            templateId: response.id,
            htmlContent: sectionHtmlContent,
            jsonContent: response.jsonContent,
            mediaQueries: htmlMediaQueries,
          });
        })
        .catch(() => {
          window.PMW.openErrorModal({message: window.i18next.t('pmwjs_unable_to_save')});
        })
        .finally(() => {
          opts.callback();
        });
    }
  );
};
